
import { useEffect, useState } from 'react';
import { Check, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import {
  PainelPermissionsDialogShell,
  PainelRoleIcon,
  CUSTOM_ROLE_VISUAL,
} from '@/components/painel/permissions/painel-role-visuals';
import {
  painelButtonPrimaryClass,
  painelPermissionCounterClass,
  painelPermissionItemDisabledClass,
  painelPermissionItemEnabledClass,
} from '@/components/painel/painel-styles';
import { PainelRocketScroll } from '@/components/painel/PainelRocketScroll';
import {
  PAINEL_ALL_PERMISSIONS,
  PAINEL_PERMISSION_GROUPS,
  PAINEL_PERMISSION_LABELS,
  type PainelPermission,
} from '@/lib/painel-rbac';
import { cn } from '@/lib/utils';

type PainelCreateRoleModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: { name: string; description: string; permissions: PainelPermission[] }) => void;
};

export function PainelCreateRoleModal({ open, onOpenChange, onCreate }: PainelCreateRoleModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<PainelPermission>>(new Set());

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setSelected(new Set());
    }
  }, [open]);

  function togglePermission(permission: PainelPermission) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(permission)) next.delete(permission);
      else next.add(permission);
      return next;
    });
  }

  function handleCreate() {
    if (!name.trim()) return;
    onCreate({
      name: name.trim(),
      description: description.trim() || 'Cargo personalizado do painel',
      permissions: Array.from(selected),
    });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[90vh] max-w-2xl overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
      >
        <PainelPermissionsDialogShell>
          <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-5 pt-6 sm:px-8">
            <div className="flex items-start gap-4 pr-14">
              <PainelRoleIcon config={CUSTOM_ROLE_VISUAL} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                  Novo cargo
                </p>
                <h2 className="mt-1 font-poppins text-xl font-bold text-white sm:text-2xl">
                  Nova permissão / cargo
                </h2>
                <p className="mt-1.5 font-inter text-sm text-slate-400">
                  Defina um cargo customizado e selecione as permissões — salvo localmente neste navegador.
                </p>
              </div>
            </div>
          </div>

          <PainelRocketScroll
            className='max-h-[min(52vh,520px)] bg-[#030023]/30'
            viewportClassName='px-6 py-5 sm:px-8 scroll-smooth'
          >
            <div className="mb-6 grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="role-name" className="text-slate-300">
                  Nome do cargo
                </Label>
                <input
                  id="role-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={painelInputClass}
                  placeholder="Ex.: Moderador de conteúdo"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="role-desc" className="text-slate-300">
                  Descrição
                </Label>
                <input
                  id="role-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={painelInputClass}
                  placeholder="Breve descrição do papel"
                />
              </div>
            </div>

            <div className="mb-3 flex items-center justify-between">
              <p className="font-poppins text-sm font-semibold text-white">Permissões</p>
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 font-inter text-[11px] font-semibold',
                  painelPermissionCounterClass
                )}
              >
                {selected.size} / {PAINEL_ALL_PERMISSIONS.length} selecionadas
              </span>
            </div>

            <div className="space-y-5">
              {PAINEL_PERMISSION_GROUPS.map((group) => (
                <section key={group.id}>
                  <h3 className="mb-2 font-poppins text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                    {group.label}
                  </h3>
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {group.permissions.map((permission) => {
                      const enabled = selected.has(permission);
                      return (
                        <li key={permission}>
                          <button
                            type="button"
                            onClick={() => togglePermission(permission)}
                            className={cn(
                              'flex w-full items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-all',
                              enabled
                                ? cn(painelPermissionItemEnabledClass, 'hover:border-sky-400/35 hover:bg-blue-950/65')
                                : cn(
                                    painelPermissionItemDisabledClass,
                                    'hover:border-sky-400/20 hover:bg-blue-950/45 hover:opacity-80'
                                  )
                            )}
                          >
                            <span
                              className={cn(
                                'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors',
                                enabled
                                  ? 'border-sky-400/50 bg-sky-500/25 text-sky-100'
                                  : 'border-white/10 bg-white/[0.03] text-transparent'
                              )}
                            >
                              <Check className="h-3.5 w-3.5" aria-hidden />
                            </span>
                            <span className="font-inter text-[13px] text-blue-50">
                              {PAINEL_PERMISSION_LABELS[permission]}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          </PainelRocketScroll>

          <div className="flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4 sm:flex-row sm:justify-end sm:px-8">
            <Button
              type="button"
              variant="ghost"
              className="h-10 rounded-xl text-slate-400 hover:bg-white/10 hover:text-white"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={!name.trim()}
              className={cn(painelButtonPrimaryClass, 'h-10 px-5')}
              onClick={handleCreate}
            >
              <Plus className="mr-2 h-4 w-4" />
              Criar cargo
            </Button>
          </div>
        </PainelPermissionsDialogShell>
      </DialogContent>
    </Dialog>
  );
}
